import { Button, message, Modal } from "antd";
import React, { useEffect, useState } from "react";

import PolicyCompletionStep, { savePolicyDrafts } from "./PolicyCompletionStep";
import { anyPolicyIncomplete } from "../../lib/policyCompleteness";

/**
 * Standalone "complete the policy before we run this" gate.
 *
 * The request composer gets this as its Details step and the multi-policy AI
 * flow gets it as step 2 of SelectPolicyItemModal. This wrapper
 * covers the remaining case: an Instant AI Fetch on a patient with exactly ONE
 * policy, which dispatches directly without any picker.
 *
 * `onCompleted` runs after the edits are saved — the host resumes whatever it was
 * about to do (dispatch the agent).
 */
export default function PolicyCompletionModal({
  open,
  onCancel,
  onCompleted,
  policy,
  patientId,
  title = "Complete policy details",
  confirmLabel = "Continue",
}) {
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(
    function () {
      if (open) setDrafts({});
    },
    [open],
  );

  const policies = policy ? [policy] : [];
  const merged = policies.map(function (p) {
    return Object.assign({}, p, drafts[p.id] || {});
  });
  const ready = merged.length > 0 && !anyPolicyIncomplete(merged);

  async function handleContinue() {
    try {
      setSaving(true);
      const ok = await savePolicyDrafts(policies, drafts);
      if (!ok) {
        message.error("Couldn't save the policy details. Please try again.");
        return;
      }
      onCompleted();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onCancel={onCancel} footer={null} title={title} width={560}>
      <PolicyCompletionStep
        policies={policies}
        drafts={drafts}
        onChange={function (policyId, field, value) {
          setDrafts(function (prev) {
            return Object.assign({}, prev, {
              [policyId]: Object.assign({}, prev[policyId], { [field]: value }),
            });
          });
        }}
        patientId={patientId}
      />
      <div style={{ display: "flex", justifyContent: "end", gap: 10, marginTop: 12 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={saving} disabled={!ready} onClick={handleContinue}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
